import React, { useEffect, useRef } from "react";
import { Tabs, type TabsRef } from "flowbite-react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTrackEvent } from "../hooks/analytics";
import Hero from "./Support/components/Hero";
import GoogleGuide from "./Support/components/GoogleGuide";
import AppleGuide from "./Support/components/AppleGuide";
import AppleSearchAdsGuide from "./Support/components/AppleSearchAdsGuide";
import QuickLinks from "./Support/components/QuickLinks";
import FAQ from "./Support/components/FAQ";
import ContactForm from "./Support/components/ContactForm";
import SupportTicketsList from "./Support/components/SupportTicketsList";

const TAB_INDEX: Record<string, number> = {
  guides: 0,
  help: 1,
  tickets: 2,
};

const OUTER_TAB_NAMES: Record<number, string> = {
  0: "integration_guides",
  1: "help_center",
  2: "support_tickets",
};

const INNER_TAB_NAMES: Record<number, string> = {
  0: "google_play_store",
  1: "apple_app_store",
  2: "apple_search_ads",
};

const Support: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const tabsRef = useRef<TabsRef>(null);
  const { trackEvent } = useTrackEvent();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && TAB_INDEX[tab] !== undefined) {
      tabsRef.current?.setActiveTab(TAB_INDEX[tab]);
    }
  }, [location.search]);

  useEffect(() => {
    // Determine target section from hash, query param, or navigation state
    let targetId = "";

    if (location.hash) {
      targetId = location.hash.replace("#", "");
    }

    if (!targetId && location.search) {
      const params = new URLSearchParams(location.search);
      targetId = params.get("section") || "";
    }

    const stateAny = location.state;
    if (!targetId && stateAny) {
      targetId = stateAny.section || stateAny.scrollTo || "";
    }

    if (targetId) {
      const el = document.getElementById(targetId);
      if (el) {
        // Smooth scroll with offset accounting for sticky headers within the support page
        const getScrollParent = (node: HTMLElement | null): HTMLElement => {
          let current: HTMLElement | null = node?.parentElement || null;
          while (current) {
            const style = window.getComputedStyle(current);
            const overflowY = style.overflowY;
            const isScrollable =
              (overflowY === "auto" || overflowY === "scroll") &&
              current.scrollHeight > current.clientHeight;
            if (isScrollable) return current;
            current = current.parentElement;
          }
          return document.scrollingElement as HTMLElement;
        };

        const scrollParent = getScrollParent(el);

        // Measure sticky tablist inside support page (defined in index.css under .support-tabs [role="tablist"]).
        const tablist = document.querySelector(
          '.support-tabs [role="tablist"]'
        ) as HTMLElement | null;

        // Base offset: height of the sticky tablist, plus a small extra gap to clear shadows/padding.
        const tablistHeight = tablist?.offsetHeight || 0;
        // index.css sets top: -8px on the tablist; add a small compensation.
        const compensation = 8;
        const extraGap = 8; // visual breathing room
        const offset = tablistHeight + compensation + extraGap;

        // Compute target position relative to the scroll container
        const parentRect = scrollParent.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const targetTop =
          elRect.top - parentRect.top + scrollParent.scrollTop - offset;

        // Ensure the scroll happens after paint for consistent measurements
        requestAnimationFrame(() => {
          scrollParent.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
          });
        });
      }
    }
  }, [location.hash, location.search, location.state]);

  return (
    <Tabs
      ref={tabsRef}
      aria-label="Support Tabs"
      style="underline"
      className="[&_[role='tab']]:backdrop-blur-md [&_[role='tab']]:bg-white/60 [&_[role='tab']]:dark:bg-neutral-900/60"
      theme={{
        tabpanel: "!p-0",
        tablist: {
          tabitem: {
            styles: {
              underline: {
                base: "rounded-t-lg bg-transparent!",
                active: {
                  on: "active rounded-t-lg border-b-2 border-secondary-500 text-secondary-600 dark:border-secondary-400 dark:text-secondary-400",
                  off: "border-b-2 border-transparent text-gray-500 hover:border-secondary-300 hover:text-secondary-600 dark:text-gray-400 dark:hover:text-secondary-300",
                },
              },
            },
          },
        },
      }}
      onActiveTabChange={(tab) =>
        trackEvent("tab_switched", {
          page: "support",
          tab_name: OUTER_TAB_NAMES[tab] ?? `tab_${tab}`,
        })
      }
    >
      <Tabs.Item title="Integration Guides" active>
        <div className="space-y-4 max-h-[calc(100vh-142px)] overflow-y-auto relative">
          <Hero />

          {/* Nested tabs for integration guides */}
          <Tabs
            className="sticky top-0 pb-2 [&_[role='tablist']]:backdrop-blur-md [&_[role='tablist']]:bg-white/40 [&_[role='tablist']]:dark:bg-neutral-900/40 [&_[role='tablist']]:rounded-xl [&_[role='tablist']]:px-2 [&_[role='tablist']]:py-1.5"
            aria-label="Integration Platform Tabs"
            style="pills"
            theme={{
              tabpanel: "!p-0",
              tablist: {
                tabitem: {
                  styles: {
                    pills: {
                      base: "",
                      active: {
                        on: "rounded-lg !bg-secondary-600 text-white dark:!bg-secondary-500",
                        off: "rounded-lg border border-secondary-500 text-secondary-600 dark:border-secondary-400 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-900/30",
                      },
                    },
                  },
                },
              },
            }}
            onActiveTabChange={(tab) =>
              trackEvent("tab_switched", {
                page: "support_integration_guides",
                tab_name: INNER_TAB_NAMES[tab] ?? `tab_${tab}`,
              })
            }
          >
            <Tabs.Item title="Google Play Store" active>
              <GoogleGuide />
            </Tabs.Item>
            <Tabs.Item title="Apple App Store">
              <AppleGuide />
            </Tabs.Item>
            <Tabs.Item title="Apple Search Ads">
              <AppleSearchAdsGuide />
            </Tabs.Item>
          </Tabs>
        </div>
      </Tabs.Item>
      <Tabs.Item title="Help Center">
        <div className="space-y-3">
          <QuickLinks />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <FAQ />
            <ContactForm />
          </div>
        </div>
      </Tabs.Item>
      <Tabs.Item title={t("support.tabs.tickets")}>
        <SupportTicketsList />
      </Tabs.Item>
    </Tabs>
  );
};

export default Support;
